import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { NavbarLeft } from '../../components/Navbars';

import { InsightDetails } from '../InsightDetails';
import { MedicareTimeline } from './components/MedicareTimeline';
import { Predictions } from '../Predictions';
import { Ai2Layout } from '../../layout/Ai2Layout';
import { WellbeingPlan } from '../../components/WellbeingPlan/WellbeingPlan';
import { InsightSettings } from '../../components/InsightSettings/InsightSettings';
import {
  getAllInsights,
  getPatientProfile,
  getAllActions,
} from '../../../store/ai2';
import { useTabSettings } from '../../hooks/useTabSettings';
import { ProfileTask } from '../../components/ProfileBar/ProfileTask/ProfileTask';
import {
  PatientProfileProvider,
  usePatientProfileSettings,
} from '../../components/ProfileBar/hooks';
import '../style.css';
import {
  PatientDetailProvider,
  useGlobalLoading,
  useTriggerUpdate,
  PatientInsightSettingsProvider,
} from '../../../context';
import { InviteMyPainHealthPatient } from '../../components/InviteMyPainHealthPatient';
import PatientDetailsContentLayout from './PatientDetailsLayout';
import type { RootState } from '../../../store';
import type { AppDispatch } from '../../../store';
import type {
  Action,
  ActionState,
  ClinicianProfileState,
  InsightState,
} from './types';
import { ActiveTabKey } from '../../components/Navbars/components/NavbarLeft/types';
import OSANavigatorApp from '../../components/Navbars/components/navItems/OSANavigatorApp';
import OSAInterventionSettings from '../../components/Navbars/components/navItems/OSAInterventionSettings';
// Removed: import { PrinterFill } from 'react-bootstrap-icons';
// Removed: import { pdfOptions } from '../../components/ProfileBar/utils/pdfOptions';
// Removed: import { generatePatientPDF } from './utils/printUtils';
import TimelinePrintBar from './components/TimelinePrintBar';

const PatientDetails: React.FC = () => {
  const { startLoading, stopLoading } = useGlobalLoading();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const access_token = searchParams.get('access_token');
  const { updatePatientProfileCount } = useTriggerUpdate();

  let initialTab: ActiveTabKey = ActiveTabKey.Timeline;
  if (window.location.hash) {
    initialTab = window.location.hash.substring(1) as ActiveTabKey;
  }

  const [activeTab, setActiveTab] = useState<ActiveTabKey>(
    initialTab as ActiveTabKey,
  );

  const { activeInsights, lastUpdatedInsight, lastAction } = useSelector(
    (state: RootState) => state.insights as InsightState,
  );
  const { actions, lastUpdatedAction } = useSelector(
    (state: RootState) => state.actions as ActionState,
  );

  const patientDetails = useSelector(
    (state: RootState) => state.patientProfile,
  );
  const { first_name, clinics } = patientDetails.patientProfile;
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile as ClinicianProfileState,
  );

  const dispatch = useDispatch<AppDispatch>();

  const { shouldDisplayLeftNavbar } = useTabSettings();

  const didLoadPatientRef = React.useRef(false);
  useEffect(() => {
    if (didLoadPatientRef.current) return;
    if (!access_token || !selectedTenancy) return;

    didLoadPatientRef.current = true;
    dispatch(getPatientProfile(access_token, selectedTenancy));
  }, [selectedTenancy, access_token, dispatch]);

  useEffect(() => {
    if (access_token && selectedTenancy) {
      dispatch(getAllActions(access_token, selectedTenancy));
      dispatch(getAllInsights(access_token, selectedTenancy));
    }
  }, [
    access_token,
    selectedTenancy,
    lastUpdatedAction,
    lastUpdatedInsight,
    lastAction,
    dispatch,
  ]);

  useEffect(() => {
    if (access_token && selectedTenancy) {
      if (updatePatientProfileCount === 0) {
        dispatch(getPatientProfile(access_token, selectedTenancy));
        return;
      }
      dispatch(getPatientProfile(access_token, selectedTenancy));
    }
  }, [updatePatientProfileCount, access_token, selectedTenancy, dispatch]);

  const { loading: patientProfileLoading } = useSelector(
    (state: RootState) => state.patientProfile,
  );

  const { isLoading: loadingTimeline } = useSelector(
    (state: RootState) => state.timeline,
  );

  useEffect(() => {
    if (loadingTimeline || patientProfileLoading) {
      startLoading();
      return;
    }
    stopLoading();
  }, [loadingTimeline, patientProfileLoading]);

  const { taskBar } = usePatientProfileSettings();
  const { showTask } = taskBar;

  const filteredActions = actions?.filter(
    (a: Action) =>
      a.action_status === 'O' || a.action_status === 'R' || !a.action_status,
  );

  const actionCount = filteredActions?.length || 0;

  return (
    <Ai2Layout>
      <PatientProfileProvider>
        <PatientDetailProvider>
          <div className="patient-detail-container ui">
            <Container fluid style={{ height: '100%' }}>
              {shouldDisplayLeftNavbar && (
                <NavbarLeft
                  sendToParent={setActiveTab}
                  activeItem={activeTab}
                  activeInsightsCnt={activeInsights || 0}
                  actionsCnt={actionCount}
                />
              )}
              <PatientInsightSettingsProvider>
                <PatientDetailsContentLayout isLoading={patientProfileLoading}>
                  {activeTab === ActiveTabKey.Timeline && (
                    <>
                      {!loadingTimeline && (
                        <TimelinePrintBar disabled={loadingTimeline} />
                      )}
                      <MedicareTimeline
                        sendToParent={setActiveTab}
                        isLoading={loadingTimeline}
                        activeItem={activeTab}
                        firstName={first_name}
                        activeInsightsCnt={activeInsights || 0}
                        actionsCnt={actionCount}
                      />
                    </>
                  )}
                  {activeTab === ActiveTabKey.InsightsAndActions && (
                    <InsightDetails />
                  )}
                  {activeTab === ActiveTabKey.Predictions && <Predictions />}
                  {activeTab === ActiveTabKey.SharedWellbeingPlan && (
                    <WellbeingPlan />
                  )}
                  {activeTab === ActiveTabKey.InsightSettings && (
                    <InsightSettings />
                  )}
                  {activeTab === ActiveTabKey.InvitePatient && (
                    <InviteMyPainHealthPatient />
                  )}
                  {activeTab === ActiveTabKey.OsaNavigatorApp && (
                    <OSANavigatorApp />
                  )}
                  {activeTab === ActiveTabKey.InterventionSettings && (
                    <OSAInterventionSettings />
                  )}
                </PatientDetailsContentLayout>
              </PatientInsightSettingsProvider>

              {showTask && (
                <ProfileTask tasks={[1, 2, 3, 4]} clinics={clinics} />
              )}
            </Container>
          </div>
        </PatientDetailProvider>
      </PatientProfileProvider>
    </Ai2Layout>
  );
};

export { PatientDetails };
